import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Lock, User, AlertCircle, KeyRound } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Eingaben wie "vpe1234", "VPE 1234" oder mit unsichtbaren Zeichen einer arabischen Tastatur
// auf die gueltige Form bringen (die Mehrheit der Fehlanmeldungen hatte diese Ursache).
const normalizeUsername = (value) => {
  const s = (value || "").replace(/[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g, "").trim();
  const m = s.match(/^vpe\s*[-_.]?\s*(\d+)$/i);
  if (m) return `VPE-${m[1]}`;
  return s.replace(/\s+/g, "");
};

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const { login, logout, user, getAuthHeaders, completePasswordChange, mustChangePassword } = useAuth();
  const navigate = useNavigate();
  const needsPasswordChange = showPasswordChange || (user && mustChangePassword);

  useEffect(() => {
    if (user && !mustChangePassword && !showPasswordChange) navigate("/dashboard", { replace: true });
  }, [user, mustChangePassword, showPasswordChange, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const result = await login(normalizeUsername(username), password);
      if (result.must_change_password) { setCurrentPassword(password); setShowPasswordChange(true); toast.info("Bitte aendern Sie Ihr Passwort beim ersten Login"); }
      else { toast.success("Willkommen im VPE Portal"); navigate("/dashboard"); }
    } catch (err) { const message = err.response?.data?.detail || "Anmeldung fehlgeschlagen"; setError(message); toast.error(message); }
    finally { setLoading(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error("Passwörter stimmen nicht überein"); return; }
    if (newPassword.length < 6) { toast.error("Neues Passwort muss mindestens 6 Zeichen haben"); return; }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPassword || password, new_password: newPassword }, { headers: getAuthHeaders() });
      completePasswordChange(); toast.success("Passwort erfolgreich geaendert"); navigate("/dashboard");
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler beim Aendern des Passworts"); }
    finally { setChangingPassword(false); }
  };

  // Ausweg, wenn die Sitzung den Pflichtwechsel verlangt, das Startpasswort aber nicht mehr bekannt ist (Omar 14.10.2026)
  const neuAnmelden = () => {
    logout(); setShowPasswordChange(false); setPassword(""); setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
  };

  if (needsPasswordChange) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4"><KeyRound className="w-8 h-8 text-amber-600" /></div>
            <h2 className="font-heading font-semibold text-2xl text-slate-900 tracking-tight">Passwort aendern</h2>
            <p className="mt-2 text-slate-500 font-body">Bitte aendern Sie Ihr Standard-Passwort, um fortzufahren.</p>
          </div>
          <form onSubmit={handlePasswordChange} className="space-y-5">
            {!password && (<div className="space-y-2"><Label className="font-heading font-medium text-sm uppercase tracking-wider text-slate-500">Aktuelles Passwort</Label><div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" /><Input type="password" className="h-12 pl-11 border-slate-300" placeholder="Aktuelles Passwort" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required /></div></div>)}
            <div className="space-y-2"><Label className="font-heading font-medium text-sm uppercase tracking-wider text-slate-500">Neues Passwort</Label><div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" /><Input type="password" className="h-12 pl-11 border-slate-300" placeholder="Neues Passwort eingeben" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required /></div></div>
            <div className="space-y-2"><Label className="font-heading font-medium text-sm uppercase tracking-wider text-slate-500">Passwort bestaetigen</Label><div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" /><Input type="password" className="h-12 pl-11 border-slate-300" placeholder="Passwort wiederholen" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required /></div></div>
            <Button type="submit" className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-heading uppercase tracking-wide rounded-md" disabled={changingPassword}>{changingPassword ? "Wird geaendert..." : "Passwort aendern"}</Button>
          </form>
          <button type="button" onClick={neuAnmelden} className="mt-4 w-full text-sm text-slate-500 hover:text-slate-900 underline font-body">Neu anmelden</button>
          <div className="mt-8 pt-6 border-t border-slate-200"><p className="text-xs text-slate-400 text-center font-body">VPE Personal GmbH &mdash; Ludwigshafen am Rhein</p></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img src="/login-bild.jpg" alt="VPE Personal" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(15, 23, 42, 0.92), rgba(15, 23, 42, 0.4))" }}>
          <div className="flex flex-col justify-end h-full p-12">
            <h1 className="font-heading font-bold text-5xl text-white tracking-tight uppercase">VPE Personal</h1>
            <p className="mt-3 text-lg text-slate-300 font-body">Mitarbeiterportal</p>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><h1 className="font-heading font-bold text-3xl tracking-tight text-slate-900 uppercase">VPE Personal</h1><p className="mt-1 text-slate-500 font-body">Mitarbeiterportal</p></div>
          <div className="mb-8"><h2 className="font-heading font-semibold text-2xl text-slate-900 tracking-tight">Anmeldung</h2><p className="mt-2 text-slate-500 font-body">Melden Sie sich mit Ihrer VPE-Personalnummer an.</p></div>
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (<div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>)}
            <div className="space-y-2"><Label htmlFor="username" className="font-heading font-medium text-sm uppercase tracking-wider text-slate-500">Benutzername</Label><div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" /><Input id="username" className="h-12 pl-11 border-slate-300" placeholder="Personalnummer" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" autoCapitalize="characters" autoCorrect="off" spellCheck={false} required /></div><p className="text-xs text-slate-400">Nur Ihre Personalnummer, ohne Name oder E-Mail</p></div>
            <div className="space-y-2"><Label htmlFor="password" className="font-heading font-medium text-sm uppercase tracking-wider text-slate-500">Passwort</Label><div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" /><Input id="password" type="password" className="h-12 pl-11 border-slate-300" placeholder="Passwort eingeben" value={password} onChange={(e) => setPassword(e.target.value)} required /></div></div>
            <Button type="submit" className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-heading uppercase tracking-wide rounded-md" disabled={loading}>{loading ? "Wird angemeldet..." : "Anmelden"}</Button>
          </form>
          <div className="mt-8 pt-6 border-t border-slate-200"><p className="text-xs text-slate-400 text-center font-body">VPE Personal GmbH &mdash; Ludwigshafen am Rhein</p></div>
        </div>
      </div>
    </div>
  );
}
